import { useEffect } from "react";

const SITE = "https://gminsuranceservices.com";

function upsertMeta(attr, key, content) {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    let created = false;
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created = true;
    }
    const prev = el.getAttribute("content");
    el.setAttribute("content", content);
    return { el, prev, created };
}

function upsertLink(rel, href) {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    let created = false;
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
        created = true;
    }
    const prev = el.getAttribute("href");
    el.setAttribute("href", href);
    return { el, prev, created };
}

// Per-page SEO for this client-rendered (CRA) app: sets <title>, meta
// description, canonical URL, Open Graph / Twitter tags, and optional JSON-LD
// (e.g. FAQPage). Restores prior values / removes added tags on unmount so each
// page's tags don't leak into the next. Pass a STABLE jsonLd reference
// (module-level const) so the effect doesn't re-run every render.
export function useSeo({ title, description, path, jsonLd }) {
    useEffect(() => {
        const url = path ? SITE + path : (typeof window !== "undefined" ? window.location.href : SITE);
        const prevTitle = document.title;
        if (title) document.title = title;

        const managed = [];
        if (description) managed.push(upsertMeta("name", "description", description));
        managed.push(upsertMeta("property", "og:title", title || document.title));
        if (description) managed.push(upsertMeta("property", "og:description", description));
        managed.push(upsertMeta("property", "og:type", "website"));
        managed.push(upsertMeta("property", "og:site_name", "GM Insurance Services"));
        managed.push(upsertMeta("property", "og:url", url));
        managed.push(upsertMeta("name", "twitter:card", "summary"));

        const canonical = upsertLink("canonical", url);

        let script = null;
        if (jsonLd) {
            script = document.createElement("script");
            script.type = "application/ld+json";
            script.setAttribute("data-seo-jsonld", "1");
            script.text = JSON.stringify(jsonLd);
            document.head.appendChild(script);
        }

        return () => {
            document.title = prevTitle;
            managed.forEach(({ el, prev, created }) => {
                if (created) el.remove();
                else if (prev !== null) el.setAttribute("content", prev);
            });
            if (canonical.created) canonical.el.remove();
            else if (canonical.prev !== null) canonical.el.setAttribute("href", canonical.prev);
            if (script) script.remove();
        };
    }, [title, description, path, jsonLd]);
}

// Build FAQPage structured data from a [{q, a}] list.
export function faqJsonLd(faqs) {
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
    };
}
